<template>
	<div class="landing-article">
		<div class="landing-article__topnav">
			<img class="landing-article__logo" :src="publisherLogo" alt="" />
			<span class="landing-article__lang">
				<InlineSvg class="landing-article__lang-icon" :src="globeIcon" />
				EN
			</span>
		</div>

		<div ref="bannerSlot" class="landing-article__banner-slot">
			<div class="landing-article__banner" :class="{ 'landing-article__banner_pinned': isBannerPinned }">
				<p class="landing-article__banner-text">The 2-minute eating window quiz</p>
				<button
					class="landing-article__banner-cta"
					type="button"
					data-control="button"
					data-button-type="continue"
					@click="onContinue('LANDING_ARTICLE_BANNER_QUIZ_BUTTON')"
				>
					Take the quiz
				</button>
			</div>
		</div>

		<header class="landing-article__header">
			<p class="landing-article__breadcrumbs">Home › Nutrition › Your Body Isn't Broken. Your Schedule Is.</p>
			<h1 class="landing-article__title">Your Body Isn't Broken. Your Schedule Is.</h1>
			<p class="landing-article__byline">
				<span class="landing-article__byline-label">Published By</span>
				<span class="landing-article__byline-author">Emma Eliott · Metabolic Health</span>
			</p>
			<div class="landing-article__meta">
				<span class="landing-article__meta-date">Last update: {{ lastUpdate }}</span>
				<span class="landing-article__meta-dot">•</span>
				<span class="landing-article__meta-item">
					<InlineSvg class="landing-article__meta-icon landing-article__meta-icon_views" :src="eyeIcon" />
					1024
				</span>
				<span class="landing-article__meta-item">
					<InlineSvg class="landing-article__meta-icon landing-article__meta-icon_time" :src="hourglassIcon" />
					6 min
				</span>
			</div>
		</header>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_INTRO_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<p class="landing-article__lead">Look at your day. Not your diet — your day.</p>

			<p class="landing-article__paragraph">
				What time did you eat breakfast? Did you skip it? What time was your last bite before bed — 8pm, 10pm, whenever
				the dishes finally got done?
			</p>

			<p class="landing-article__paragraph">
				Most people trying to lose weight can recite calorie counts, carb grams, and “good” versus “bad” foods on
				command. Almost nobody can tell you their eating window. And that gap — not willpower, not discipline, not
				what's on the plate —
				<b>is where most weight-loss plans quietly fall apart.</b>
			</p>

			<blockquote class="landing-article__pull-quote landing-article__pull-quote_md">
				A 12-week University of Granada trial found people following an 8-hour eating window lost similar amounts of
				weight —
				<b>no matter what time of day the window started.</b>
				What mattered was whether they kept doing it.
			</blockquote>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_HOOK_QUOTE_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<blockquote class="landing-article__dark-quote">
				“The plan you stay on is the one that changes your weight.”
			</blockquote>

			<div class="landing-article__figure">
				<img
					class="landing-article__figure-image landing-article__figure-image_cover"
					:src="hero"
					alt=""
					loading="lazy"
				/>
			</div>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_DIET_SECTION_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<h2 class="landing-article__heading">The Diet Wasn't the Problem</h2>

			<p class="landing-article__paragraph">
				Keto. Low-carb. Counting every calorie in an app until it feels like a second job. Cutting out entire food
				groups, then adding them back six weeks later out of sheer exhaustion.
			</p>

			<p class="landing-article__paragraph">
				It's that, at some point, they stop working for you because they weren't built to survive real life — a work
				trip, a stressful month, or a holiday table.
			</p>

			<p class="landing-article__paragraph">
				Researchers studying weight loss have circled the same finding for years: the diet that wins isn't the “best”
				one on paper.
				<b>It's the one a person can actually stick to.</b>
			</p>

			<p class="landing-article__stat-callout">
				About a third of participants were still following their eating window a full year later — notably higher than
				the drop-off typically seen with calorie-counting approaches.
			</p>

			<p class="landing-article__paragraph">
				That's the part most diet plans never solve. They tell you what to eat. They almost never build a schedule your
				actual life can hold onto.
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_SCIENCE_SECTION_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<h2 class="landing-article__heading">The Missing Piece Most Diets Ignore</h2>

			<p class="landing-article__paragraph">
				This is where time-restricted eating comes in — not as a trend, but as one of the more studied ideas in
				weight-management research over the past few years.
			</p>

			<p class="landing-article__paragraph">
				The concept is almost surprisingly simple: your body isn't a machine that treats food the same at 7am and 11pm.
				Insulin sensitivity, digestion, and fat metabolism shift across the day, tied to your internal circadian clock.
			</p>

			<p class="landing-article__paragraph">
				Time-restricted eating appears to offer metabolic advantages tied to aligning meals with that circadian rhythm,
				somewhat independent of weight loss itself.
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_CIRCADIAN_FIGURE_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<div class="landing-article__figure">
				<img
					class="landing-article__figure-image landing-article__figure-image_contain"
					:src="circadianCycle"
					alt=""
					loading="lazy"
				/>
			</div>

			<!-- prettier-ignore -->
			<p class="landing-article__paragraph">
				Compress your eating into a consistent window, and you're not just eating less by accident — people
				following an 8-hour eating window have shown modest reductions in body weight and mild improvements in
				metabolic health, largely from an unplanned drop in daily calorie intake of roughly
				<b>200–550 calories</b>, without anyone consciously “dieting.”
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_STAT_TILES_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<div class="landing-article__tiles">
				<div class="landing-article__tile">
					<p class="landing-article__tile-value">
						200–550
						<span class="landing-article__tile-unit">cal/day</span>
					</p>
					<p class="landing-article__tile-label">unplanned intake drop</p>
				</div>
				<div class="landing-article__tile">
					<p class="landing-article__tile-value">Circadian</p>
					<p class="landing-article__tile-label">
						meals aligned with your circadian rhythm, independent of weight loss
					</p>
				</div>
			</div>

			<p class="landing-article__paragraph">
				To be fair to the science: this isn't magic, and it isn't a bypass on the basic math of energy in versus energy
				out. Some clinical trials have found that structured fasting windows aren't meaningfully more effective than
				standard daily calorie restriction for short-term weight loss.
			</p>

			<p class="landing-article__paragraph">
				What the research does suggest, consistently, is something less flashy but arguably more useful: a well-matched
				eating window is easier to stay on than a plan built around constant tracking and denial.
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_RESEARCH_QUESTION_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<blockquote class="landing-article__pull-quote landing-article__pull-quote_lg">
				Which raises the real question. Not “does this work” — but “what window actually fits your life, your age, and
				your routine?”
			</blockquote>

			<p class="landing-article__paragraph">
				Because that part isn't universal. And guessing at it is exactly why so many people quit in week three.
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_PERSONALIZATION_SECTION_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<h2 class="landing-article__heading">The Personalization Problem</h2>

			<p class="landing-article__paragraph">
				Here's the honest catch nobody puts on the label: an eating window built for a 25-year-old software developer
				who eats dinner at 9pm doesn't work for a
				<b>52-year-old nurse on rotating shifts.</b>
				A schedule designed around a desk job falls apart against a body that's dealt with years of yo-yo dieting,
				slowed metabolism, or hormonal shifts.
			</p>

			<p class="landing-article__paragraph">
				Your age changes how your metabolism responds. Your current weight changes how much of a deficit you actually
				need. Your daily routine — when you wake up, when you're most stressed, when you're around food out of boredom
				rather than hunger — determines whether a window is realistic or
				<b>one more thing you'll abandon by Friday.</b>
			</p>

			<p class="landing-article__paragraph">
				That's the actual gap between people who try intermittent fasting and quit, and people who try it and it quietly
				becomes normal. It was never about motivation. It was about whether the plan matched the person.
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_QUIZ_CTA_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<h2 class="landing-article__heading">Finding the right eating window for you</h2>

			<p class="landing-article__paragraph">
				It starts with a few simple questions about your age, routine, goals, and lifestyle.
			</p>

			<p class="landing-article__paragraph">
				Based on your answers, we'll recommend an eating window designed to fit your life — not someone else's.
			</p>

			<div class="landing-article__cta-box landing-article__cta-box_inline">
				<div class="landing-article__cta-box-copy">
					<p class="landing-article__cta-box-title">TAKE THE QUIZ</p>
					<p class="landing-article__cta-box-text">
						Two minutes, a handful of honest answers — a personalized eating window and plan based on your age, weight,
						and routine, not a generic template.
					</p>
				</div>
				<button
					class="landing-article__cta"
					type="button"
					data-control="button"
					data-button-type="continue"
					@click="onContinue('LANDING_ARTICLE_INLINE_QUIZ_BUTTON')"
				>
					Take the Quiz
				</button>
			</div>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_CONCLUSION_SECTION_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<h2 class="landing-article__heading">The Best Weight Loss Plan Is the One You'll Actually Stick To</h2>

			<p class="landing-article__paragraph">
				If you've been burned by “revolutionary” plans before, that skepticism is fair, and it's worth keeping.
				Time-restricted eating isn't a loophole around eating well, and it isn't guaranteed to outperform every other
				approach for every person.
			</p>

			<p class="landing-article__paragraph">
				Some research has even raised questions about long-term fasting regimens and cardiovascular risk, particularly
				around loss of lean muscle mass and how the extra hunger gets managed. Any plan worth trusting will tell you
				that up front rather than promising the moon.
			</p>

			<p class="landing-article__paragraph">
				What the evidence does support is narrower, but real: a consistent, personalized eating window, matched to your
				actual life, is one of the more sustainable levers available — because the biggest predictor of results isn't
				the “perfect” method.
				<b>It's whether you're still doing it in six months.</b>
			</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_CLOSING_QUOTE_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<blockquote class="landing-article__pull-quote landing-article__pull-quote_sm">
				That's the whole bet behind the quiz.
				<b>Not a magic number. A schedule you can actually keep.</b>
			</blockquote>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_NATIVE_AD_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<div class="landing-article__native-ad">
				<img class="landing-article__native-ad-image" :src="nativeAd" alt="" loading="lazy" />
				<p class="landing-article__native-ad-lines">
					<span>2 minutes.</span>
					<span>A schedule that finally fits your life.</span>
				</p>
				<p class="landing-article__native-ad-source">Daily Wellness</p>
			</div>

			<p class="landing-article__disclaimer">Results may vary due to personal features.</p>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_FINAL_CTA_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<div class="landing-article__cta-box landing-article__cta-box_final">
				<div class="landing-article__cta-box-copy">
					<p class="landing-article__cta-box-title">TAKE THE FREE QUIZ — DISCOVER YOUR EATING WINDOW TYPE</p>
				</div>
				<button
					class="landing-article__cta"
					type="button"
					data-control="button"
					data-button-type="continue"
					@click="onContinue('LANDING_ARTICLE_FINAL_QUIZ_BUTTON')"
				>
					Take the Quiz
				</button>
			</div>
		</BlockVisibilityObserver>

		<BlockVisibilityObserver
			class="landing-article__block"
			title="LANDING_ARTICLE_COMMENTS_BLOCK"
			:path="analyticsPath"
			:threshold="1"
		>
			<section class="landing-article__comments">
				<p class="landing-article__comments-title">Comments (6)</p>

				<div class="landing-article__comment">
					<div class="landing-article__comment-head">
						<img class="landing-article__comment-avatar" :src="avatar1" alt="" loading="lazy" />
						<span class="landing-article__comment-name">Marina K.</span>
						<span class="landing-article__comment-date">2 Aug 2026</span>
					</div>
					<p class="landing-article__comment-text">
						The quiz recommended an eating window that fit my routine instead of giving me the same plan everyone else
						gets. That made a lot more sense.
					</p>
				</div>

				<div class="landing-article__comment">
					<div class="landing-article__comment-head">
						<img class="landing-article__comment-avatar" :src="avatar2" alt="" loading="lazy" />
						<span class="landing-article__comment-name">David R.</span>
						<span class="landing-article__comment-date">1 Aug 2026</span>
					</div>
					<p class="landing-article__comment-text">
						Most articles tell you what to eat. This one actually explained why my daily routine matters — and why one
						eating schedule doesn't fit everyone.
					</p>
				</div>

				<div class="landing-article__comment">
					<div class="landing-article__comment-head">
						<img class="landing-article__comment-avatar" :src="avatar3" alt="" loading="lazy" />
						<span class="landing-article__comment-name">Priya S.</span>
						<span class="landing-article__comment-date">30 min ago</span>
					</div>
					<p class="landing-article__comment-text">
						Never thought about it as a scheduling problem before. Kind of makes sense why nothing's stuck so far. Worth
						finding out my actual window.
					</p>
				</div>

				<button
					class="landing-article__cta landing-article__cta_block"
					type="button"
					data-control="button"
					data-button-type="continue"
					@click="onContinue('LANDING_ARTICLE_COMMENTS_QUIZ_BUTTON')"
				>
					Take the Quiz
				</button>
			</section>
		</BlockVisibilityObserver>
	</div>
</template>

<script>
import Jet, { titleEnumExists, trackingHeader } from '@/modules/Jet'
import { dateProvider } from '@/helpers/date'
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import publisherLogo from '@/assets/landing-article/health-post-logo.png'
import hero from '@/assets/landing-article/hero.png'
import circadianCycle from '@/assets/landing-article/circadian-cycle.png'
import nativeAd from '@/assets/landing-article/native-ad.png'
import avatar1 from '@/assets/landing-article/avatar-1.png'
import avatar2 from '@/assets/landing-article/avatar-2.png'
import avatar3 from '@/assets/landing-article/avatar-3.png'
import globeIcon from '@/assets/landing-article/globe.svg'
import eyeIcon from '@/assets/landing-article/eye.svg'
import hourglassIcon from '@/assets/landing-article/hourglass.svg'

const ANALYTICS_PATH = ['OnboardingWelcomeScreen']

export default {
	name: 'LandingArticle',

	components: {
		BlockVisibilityObserver,
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return {
			analyticsPath: ANALYTICS_PATH,
			publisherLogo,
			hero,
			circadianCycle,
			nativeAd,
			avatar1,
			avatar2,
			avatar3,
			globeIcon,
			eyeIcon,
			hourglassIcon,
		}
	},

	data() {
		return {
			isBannerPinned: false,
		}
	},

	computed: {
		lastUpdate() {
			return dateProvider.toShortMonthDay(new Date())
		},
	},

	mounted() {
		window.addEventListener('scroll', this.handleScroll, { passive: true })
		this.handleScroll()
	},

	beforeUnmount() {
		window.removeEventListener('scroll', this.handleScroll)
	},

	methods: {
		handleScroll() {
			this.isBannerPinned = this.$refs.bannerSlot.getBoundingClientRect().top <= 0
		},

		onContinue(buttonTitle) {
			this.$analytic.logEvent('ButtonClick', { title: buttonTitle })

			if (titleEnumExists(buttonTitle)) {
				Jet.track('ButtonClick', {}, trackingHeader(buttonTitle))
			}

			this.$emit('progressLoaderCompleted')
		},
	},
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

@function fig($px) {
	@return math.div($px, 16) * 1rem;
}

$surface: #f5f5f7;
$ink: #1a1a1a;
$body-ink: #2e2e33;
$muted-ink: #6b6b70;
$hairline: rgba(0, 0, 0, 0.08);
$gutter: fig(16);

.landing-article {
	display: flex;
	flex-direction: column;
	gap: fig(14);
	margin-left: -20px;
	margin-right: -20px;
	padding-left: $gutter;
	padding-right: $gutter;
	background: var(--Background-Primary);

	@media (max-width: 340px) {
		margin-left: -15px;
		margin-right: -15px;
	}

	&__block {
		display: flex;
		flex-direction: column;
		gap: fig(14);
	}

	&__topnav,
	&__banner-slot,
	&__dark-quote {
		margin-left: -$gutter;
		margin-right: -$gutter;
	}

	&__topnav,
	&__banner-slot {
		margin-bottom: -#{fig(14)};
	}

	&__topnav {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: fig(14) fig(20);
		background: var(--Background-Primary);
		border-bottom: 1px solid #e0e0e5;
	}

	&__logo {
		display: block;
		width: fig(95);
		aspect-ratio: 95 / 40;
		object-fit: contain;
		border-radius: fig(6);
	}

	&__lang {
		display: inline-flex;
		align-items: center;
		gap: fig(4);
		font-size: fig(16);
		font-weight: 600;
		line-height: 1.25;
		color: var(--Text-Primary-100);
	}

	&__lang-icon {
		flex-shrink: 0;
		width: fig(24);
		height: fig(24);
	}

	&__banner-slot {
		min-height: fig(72);
	}

	&__banner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: fig(16);
		padding: fig(12) fig(16);
		background: var(--Fill-Base);

		&_pinned {
			position: fixed;
			top: 0;
			left: 50%;
			z-index: 2;
			width: 100%;
			max-width: 500px;
			transform: translateX(-50%);
		}
	}

	&__banner-text {
		margin: 0;
		font-size: fig(14);
		font-weight: 700;
		line-height: fig(16);
		text-transform: uppercase;
		color: var(--Text-Primary-100);
	}

	&__banner-cta {
		flex-shrink: 0;
		height: fig(48);
		padding: fig(12);
		border: none;
		border-radius: fig(12);
		background: var(--Fill-Rose);
		font-size: fig(14);
		font-weight: 700;
		line-height: fig(16);
		text-transform: uppercase;
		white-space: nowrap;
		color: var(--Text-On-Color);
	}

	&__header {
		display: flex;
		flex-direction: column;
		gap: fig(12);
		padding-top: fig(18);
	}

	&__breadcrumbs {
		margin: 0;
		font-size: fig(11);
		font-weight: 400;
		line-height: fig(13);
		color: var(--Text-Primary-60);
	}

	&__title {
		margin: 0;
		font-size: fig(30);
		font-weight: 700;
		line-height: fig(38.4);
		color: $ink;
	}

	&__byline {
		display: flex;
		gap: fig(8);
		margin: 0;
		font-size: fig(11);
		line-height: fig(13);
	}

	&__byline-label {
		font-weight: 400;
		color: var(--Text-Primary-60);
	}

	&__byline-author {
		font-weight: 600;
		color: var(--Text-Accent);
	}

	&__meta {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		margin-top: fig(8);
	}

	&__meta-date {
		font-size: fig(12);
		font-weight: 400;
		line-height: fig(22);
		color: var(--Text-Primary-100);
	}

	&__meta-dot {
		margin: 0 fig(10);
		font-size: fig(12);
		font-weight: 400;
		line-height: fig(22);
		color: var(--Text-Primary-60);
	}

	&__meta-item {
		display: inline-flex;
		align-items: center;
		gap: fig(4);
		font-size: fig(11);
		font-weight: 400;
		line-height: fig(13);
		color: var(--Text-Primary-60);

		& + & {
			margin-left: fig(7);
		}
	}

	&__meta-icon {
		flex-shrink: 0;

		&_views {
			width: fig(16);
			height: fig(16);
		}

		&_time {
			width: fig(10);
			height: fig(12.5);
		}
	}

	&__heading {
		margin: 0;
		font-size: fig(20);
		font-weight: 700;
		line-height: 1.24;
		color: var(--Text-Primary-100);
	}

	&__lead,
	&__paragraph {
		margin: 0;
		font-size: fig(17);
		line-height: fig(28);
		color: var(--Text-Primary-100);
	}

	&__lead {
		font-weight: 600;
	}

	&__paragraph {
		font-weight: 400;
	}

	&__pull-quote {
		margin: 0;
		padding: fig(12) fig(14);
		background: $surface;
		border-left: 3px solid var(--Fill-Accent-100);
		border-radius: fig(4) fig(16) fig(16) fig(4);
		font-style: italic;
		color: var(--Text-Primary-100);

		&_lg {
			font-size: fig(17);
			font-weight: 500;
			line-height: fig(26);
		}

		&_md {
			margin-top: fig(10);
			font-size: fig(16);
			font-weight: 500;
			line-height: fig(24);
		}

		&_sm {
			min-height: fig(90);
			font-size: fig(14);
			font-weight: 400;
			line-height: 1.6;

			b {
				font-weight: 600;
			}
		}
	}

	&__stat-callout {
		margin: fig(10) 0;
		padding: fig(12) fig(16);
		background: $surface;
		border-radius: fig(12);
		font-size: fig(14);
		font-weight: 600;
		line-height: 1.6;
		color: $ink;
	}

	&__dark-quote {
		display: flex;
		align-items: center;
		justify-content: center;
		min-height: fig(128);
		margin-top: fig(26);
		margin-bottom: fig(18);
		padding: fig(8) fig(12);
		background: var(--Fill-Inverse);
		font-size: fig(21);
		font-weight: 700;
		line-height: 1.32;
		text-align: center;
		color: var(--Text-White-100);
	}

	&__figure {
		display: flex;
		align-items: center;
		justify-content: center;
		aspect-ratio: 358 / 200;
		overflow: hidden;
		border: 1px solid $hairline;
		border-radius: fig(16);
	}

	&__figure-image {
		display: block;

		&_cover {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		&_contain {
			width: math.percentage(math.div(248, 358));
			aspect-ratio: 248 / 187;
			object-fit: contain;
		}
	}

	&__tiles {
		display: flex;
		gap: fig(12);
	}

	&__tile {
		display: flex;
		flex: 1 0 0;
		flex-direction: column;
		gap: fig(4);
		min-width: 0;
		padding: fig(12) fig(14);
		background: $surface;
		border-radius: fig(12);
	}

	&__tile-value {
		margin: 0;
		font-size: fig(20);
		font-weight: 700;
		line-height: fig(24);
		color: var(--Text-Primary-100);
	}

	&__tile-unit {
		font-size: fig(12);
		font-weight: 400;
		color: var(--Text-Primary-60);
	}

	&__tile-label {
		margin: 0;
		font-size: fig(12);
		font-weight: 400;
		line-height: fig(15);
		color: var(--Text-Primary-60);
	}

	&__cta-box {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: fig(16);
		text-align: center;

		&_inline {
			margin-top: fig(6);
			margin-bottom: fig(26);
			padding: fig(20) fig(18);
			background: var(--Fill-Base);
			border-radius: fig(16);
		}

		&_final {
			padding: fig(22) fig(18);
			background: $surface;
			border-radius: fig(10);
		}
	}

	&__cta-box-copy {
		display: flex;
		flex-direction: column;
		gap: fig(12);
		width: 100%;
	}

	&__cta-box-title {
		margin: 0;
		font-size: fig(17);
		font-weight: 700;
		line-height: 1.24;
		color: var(--Text-Primary-100);
	}

	&__cta-box-text {
		margin: 0;
		font-size: fig(15);
		font-weight: 400;
		line-height: fig(24);
		color: $body-ink;
	}

	&__cta {
		height: fig(45);
		padding: fig(12) fig(32);
		border: none;
		border-radius: fig(12);
		background: var(--Fill-Rose);
		font-size: fig(14);
		font-weight: 700;
		line-height: 1.5;
		white-space: nowrap;
		color: var(--Text-On-Color);

		&_block {
			width: 100%;
		}
	}

	&__native-ad {
		position: relative;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		aspect-ratio: 358 / 200;
		padding: fig(18);
		overflow: hidden;
	}

	&__native-ad-image {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__native-ad-lines {
		position: relative;
		display: flex;
		flex-direction: column;
		margin: 0;
		font-size: fig(15);
		font-weight: 700;
		line-height: 1.28;
		color: var(--Text-White-100);
	}

	&__native-ad-source {
		position: relative;
		margin: 0;
		font-size: fig(11);
		font-weight: 700;
		line-height: 1.5;
		color: var(--Text-White-100);
	}

	&__disclaimer {
		margin: 0;
		font-size: fig(10.5);
		font-weight: 400;
		font-style: italic;
		line-height: 1.5;
		color: $muted-ink;
	}

	&__comments {
		display: flex;
		flex-direction: column;
		gap: fig(16);
		padding-top: fig(14);
		padding-bottom: fig(24);
	}

	&__comments-title {
		margin: 0;
		font-size: fig(17);
		font-weight: 700;
		line-height: 1.5;
		color: $ink;
	}

	&__comment {
		display: flex;
		flex-direction: column;
		gap: fig(6);
	}

	&__comment-head {
		display: grid;
		grid-template-columns: auto 1fr;
		align-items: center;
		column-gap: fig(8);
		row-gap: fig(1);
		line-height: 1.2;
	}

	&__comment-avatar {
		grid-row: 1 / span 2;
		width: fig(28);
		aspect-ratio: 1;
		border-radius: 50%;
		object-fit: cover;
	}

	&__comment-name {
		font-size: fig(13);
		font-weight: 600;
		color: $ink;
	}

	&__comment-date {
		font-size: fig(11);
		font-weight: 400;
		color: $muted-ink;
	}

	&__comment-text {
		margin: 0;
		font-size: fig(15);
		font-weight: 400;
		line-height: 1.48;
		color: $body-ink;
	}
}
</style>
